import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import {
   Component,
   DestroyRef,
   ElementRef,
   EventEmitter,
   Input,
   OnChanges,
   Output,
   SimpleChanges,
   NgZone
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import {
   ReportRowCommentAddedEvent,
   ReportRowCommentsModalComponent
} from './report-row-comments-modal/report-row-comments-modal.component';
import { DropdownModule } from 'primeng/dropdown';
import { getReportDefinition } from './report-definitions/registry';
import {
   ReportColumn,
   ReportDefinition,
   ReportHeaderConfig,
   ReportHeaderContext,
   ReportHeaderSnippet,
   ReportLayoutDefinition,
   ReportRowAnnotationConfig,
   ReportRowCheckConfig,
   ReportRowDetailField,
   ReportRowDetailsConfig
} from './models/report-definition';
import { ReportRowVm } from './models/report-row-vm';
import { CalendarModule } from 'primeng/calendar';
import {
   EMPTY_HTML_REPORT_ANNOTATIONS,
   HtmlReportAnnotations,
   HtmlCommentAnnotation,
   HtmlHighlightAnnotation,
   HtmlReportRowActivityCount
} from '../../domain/html-report-annotations';
import { HtmlReportRowActivityCountStoreService } from '../../services/stores/html-report-row-activity-count-store.service';
import { TaskService } from '../../services/task.service';
import { MentionStoreService } from '../../services/stores/mention-store.service';
import { MessageService } from 'primeng/api';
import { applyReportFilters } from './report-definitions/report-filtering.engine';
import { applyReportSorting } from './report-definitions/report-sorting.engine';
import {
   FilterCriterion,
   FilterFieldDef,
   FilterOperator
} from './models/report-filtering';
import { ReportSortingConfig, ReportSortState } from './models/report-sorting';
import { ReportSummaryHeaderComponent } from './report-summary-header/report-summary-header.component';
import { formatCurrency } from './utils/report-formatting.utils';
import { getReportMeta, getRiskSummary } from './utils/report-json.utils';
import { ReportRiskSummaryComponent } from './report-risk-summary/report-risk-summary.component';
import { ReportLayoutTabsComponent } from './report-layout-tabs/report-layout-tabs.component';
import { HtmlReportSupportingFileCountStoreService } from '../../services/stores/html-report-supporting-file-count-store.service';
import {
   attachReportRowRisks,
   getReportRowRiskSeverityClass
} from './utils/report-risk.utils';

import {
   attachReportRowRiskResolutions,
   clearReportRowRiskResolutions,
   isReportRowRiskResolutionEligible
} from './utils/report-risk-resolution.utils';
import {
   getPersistedPmsReportRowIds,
   isReportRowSupportingFileEligible
} from './utils/report-row-supporting-file.utils';

import {
   ReportRowRiskResolutionChangedEvent,
   ReportRowRiskResolutionModalComponent
} from './report-row-risk-resolution-modal/report-row-risk-resolution-modal.component';

import {
   HtmlReportRiskResolutionLoadStatus,
   HtmlReportRiskResolutionMutationStatus,
   HtmlReportRiskResolutionStoreService
} from '../../services/stores/html-report-risk-resolution-store.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PmsRiskResolution } from '../../domain/pms-risk-resolution';
import { PmsResolvedFinancialRiskResponse } from '../../domain/pms-resolved-financial-risk';
import {
   HtmlReportResolvedFinancialRiskLoadStatus,
   HtmlReportResolvedFinancialRiskStoreService
} from '../../services/stores/html-report-resolved-financial-risk-store.service';
import { ReportRowSupportingFilesModalComponent } from './report-row-supporting-files-modal/report-row-supporting-files-modal.component';
import {
   PMS_ROW_SUPPORTING_FILE_COUNT_CHANNEL,
   isPmsRowSupportingFileCountChangedMessage
} from '../../domain/pms-row-supporting-file-count-event';

const PHOBOS_TASK_INSTANCE_PMS_DATA_STATUSES = {
   PENDING_SOURCE: 'PENDING_SOURCE',
   READY_FOR_GENERATION: 'READY_FOR_GENERATION',
   POPULATED: 'POPULATED',
   NO_FINDINGS: 'NO_FINDINGS',
   NO_SOURCE_DATA: 'NO_SOURCE_DATA',
   GENERATION_FAILED: 'GENERATION_FAILED'
} as const;

@Component({
   selector: 'app-html-report-viewer',
   standalone: true,
   imports: [
      CommonModule,
      FormsModule,
      ButtonModule,
      DropdownModule,
      CalendarModule,
      ReportSummaryHeaderComponent,
      ReportRiskSummaryComponent,
      ReportLayoutTabsComponent,
      ReportRowCommentsModalComponent,
      ReportRowRiskResolutionModalComponent,
      ReportRowSupportingFilesModalComponent
   ],
   templateUrl: './html-report-viewer.component.html',
   styleUrls: ['./html-report-viewer.component.scss']
})
export class HtmlReportViewerComponent implements OnChanges {
   @Input() reportType!: string;
   @Input() reportData: any;
   @Input() seedAnnotations: any;
   @Input() storageKey?: string;
   @Input() taskInstanceId!: number;
   @Input() pmsDataId: number | null = null;
   @Input() targetRowId: string | null = null;
   @Input() useApiAnnotations = true;
   @Input() businessDate?: string | Date | null;
   @Input() pmsDataStatus?: string | null;
   @Input() reportHeaderActionLabel = '';
   @Input() reportHeaderActionDisabled = false;

   @Output() reportHeaderActionRequested = new EventEmitter<void>();

   annotations: HtmlReportAnnotations = EMPTY_HTML_REPORT_ANNOTATIONS;
   isAnnotationsLoading = false;

   // =======================
   // ADD HERE: Table sizing + truncation config (global defaults)
   // =======================
   readonly defaultColumnWidth = '160px';
   readonly defaultCellCharLimit = 100;

   // Track expanded cells: key = `${rowId}::${colKey}`
   private expandedCells = new Set<string>();

   /**
    * Expanded reservation-detail rows.
    *
    * This is temporary view state and is deliberately not persisted.
    */
   private expandedDetailRowIds = new Set<string>();

   // local lookup maps for fast UI
   private highlightByRowId = new Map<string, HtmlHighlightAnnotation>();
   private commentByRowId = new Map<string, HtmlCommentAnnotation>();

   private pendingRowOps = new Set<string>();
   private riskResolutions: PmsRiskResolution[] = [];
   private riskResolutionClickTimer: ReturnType<typeof setTimeout> | null = null;
   private pendingQuickResolutionRowId: string | null = null;
   riskResolutionMutationStatus: HtmlReportRiskResolutionMutationStatus = 'IDLE';

   private hasScrolledToMentionTarget = false;

   riskResolutionLoadStatus: HtmlReportRiskResolutionLoadStatus = 'IDLE';
   riskResolutionErrorStatus: number | null = null;
   resolvedFinancialRiskLoadStatus: HtmlReportResolvedFinancialRiskLoadStatus =
      'IDLE';
   private resolvedFinancialRisk: PmsResolvedFinancialRiskResponse | null = null;

   checkedRowIds = new Set<string>();

   private readonly defaultExcludedCheckRowTypes = new Set<string>([
      'groupHeader',
      'groupTotal',
      'sectionHeader',
      'sectionTotal',
      'summary',
      'summaryTotal',
      'grandTotal',
      'cashierTotal'
   ]);

   private readonly defaultExcludedAnnotationRowTypes = new Set<string>([
      'groupHeader',
      'groupTotal',
      'sectionHeader',
      'sectionTotal',
      'summary',
      'summaryTotal',
      'grandTotal',
      'cashierTotal'
   ]);

   readonly defaultHighlightColor = '#FFF3CD';

   readonly joinOptions = [
      { label: 'AND', value: 'AND' },
      { label: 'OR', value: 'OR' }
   ];

   definition: ReportDefinition | null = null;
   activeLayout: ReportLayoutDefinition | null = null;
   activeLayoutId: string | null = null;
   activeColumns: ReportColumn[] = [];
   annotationDisabledLayoutIds = new Set<string>();

   allRows: ReportRowVm[] = [];
   visibleRows: ReportRowVm[] = [];
   filterCriteria: FilterCriterion[] = [];
   isFilterOpen = false;
   activeSortState: ReportSortState | null = null;

   reportHeaderTitle = '';
   reportHeaderBusinessDateLabel = 'Business Date';
   reportHeaderBusinessDateText = '';
   reportHeaderSnippets: ReportHeaderSnippet[] = [];

   isCommentModalVisible = false;
   activeCommentRowId: string | null = null;
   isRiskResolutionModalVisible = false;
   activeRiskResolutionRowId: string | null = null;
   hasRiskResolutionEligibleRows = false;
   hasSupportingFileEligibleRows = false;
   private persistedSupportingFileRowIds: ReadonlySet<string> = new Set<string>();
   supportingFileCountsByRowId: ReadonlyMap<string, number> = new Map();
   rowActivityCountsByRowId: ReadonlyMap<string, HtmlReportRowActivityCount> =
      new Map();
   isSupportingFilesModalVisible = false;
   activeSupportingFilesRowId: string | null = null;

   constructor(
      private taskService: TaskService,
      private mentionStore: MentionStoreService,
      private messageService: MessageService,
      private router: Router,
      private htmlReportRiskResolutionStore: HtmlReportRiskResolutionStoreService,
      private htmlReportResolvedFinancialRiskStore: HtmlReportResolvedFinancialRiskStoreService,
      private htmlReportSupportingFileCountStore: HtmlReportSupportingFileCountStoreService,
      private htmlReportRowActivityCountStore: HtmlReportRowActivityCountStoreService,
      private destroyRef: DestroyRef,
      private ngZone: NgZone,
      private elementRef: ElementRef<HTMLElement>
   ) {
      this.htmlReportResolvedFinancialRiskStore.state$
         .pipe(takeUntilDestroyed(this.destroyRef))
         .subscribe((state) => {
            this.resolvedFinancialRiskLoadStatus = state.status;
            this.resolvedFinancialRisk = state.result;
            this.rebuildReportHeader();
         });

      this.htmlReportRiskResolutionStore.state$
         .pipe(takeUntilDestroyed(this.destroyRef))
         .subscribe((state) => {
            this.riskResolutionLoadStatus = state.status;
            this.riskResolutionErrorStatus = state.errorStatus;
            this.riskResolutions = state.resolutions;

            this.applyRiskResolutionState();
         });

      this.htmlReportRiskResolutionStore.mutationState$
         .pipe(takeUntilDestroyed(this.destroyRef))
         .subscribe((state) => {
            this.riskResolutionMutationStatus = state.status;

            // Refresh the authoritative monetary calculation for both the
            // quick action and selective modal. Conflict means local state
            // was stale, so the amount must also be refreshed.
            if (
               state.status === 'SUCCESS' ||
               (state.status === 'ERROR' && state.errorStatus === 409)
            ) {
               this.htmlReportResolvedFinancialRiskStore.refresh();
            }

            if (!this.pendingQuickResolutionRowId || state.status === 'SAVING') {
               return;
            }

            if (state.status === 'SUCCESS' && state.response) {
               this.onRiskResolutionChanged({
                  rowId: this.pendingQuickResolutionRowId,
                  response: state.response
               });
            } else if (state.status === 'ERROR') {
               this.messageService.add({
                  severity: 'error',
                  summary: 'Risk resolution failed',
                  detail:
                     state.errorStatus === 409
                        ? 'Risk resolution was changed by another user. The latest state is being loaded.'
                        : state.errorMessage || 'Unable to update risk resolution.'
               });
            }

            this.pendingQuickResolutionRowId = null;
         });

      this.destroyRef.onDestroy(() => this.cancelRiskResolutionClick());

      this.htmlReportSupportingFileCountStore.state$
         .pipe(takeUntilDestroyed(this.destroyRef))
         .subscribe((state) => {
            this.supportingFileCountsByRowId = state.countsByRowId;
         });

      this.htmlReportRowActivityCountStore.state$
         .pipe(takeUntilDestroyed(this.destroyRef))
         .subscribe((state) => {
            this.rowActivityCountsByRowId = state.countsByRowId;
         });

      if (typeof BroadcastChannel !== 'undefined') {
         const channel = new BroadcastChannel(PMS_ROW_SUPPORTING_FILE_COUNT_CHANNEL);

         channel.onmessage = (event: MessageEvent<unknown>) => {
            if (!isPmsRowSupportingFileCountChangedMessage(event.data)) {
               return;
            }

            const message = event.data;

            this.ngZone.run(() => {
               if (
                  this.taskInstanceId !== message.taskInstanceId ||
                  this.pmsDataId !== message.pmsDataId ||
                  !this.persistedSupportingFileRowIds.has(message.targetRowId)
               ) {
                  return;
               }

               this.htmlReportSupportingFileCountStore.setRowCount(
                  message.taskInstanceId,
                  message.pmsDataId,
                  message.targetRowId,
                  message.fileCount
               );
            });
         };

         this.destroyRef.onDestroy(() => {
            channel.onmessage = null;
            channel.close();
         });
      }
   }

   ngOnChanges(changes: SimpleChanges): void {
      if (changes['targetRowId']) {
         this.hasScrolledToMentionTarget = false;
      }

      if (changes['taskInstanceId']) {
         this.loadRowActivityCounts();
      }

      if (changes['taskInstanceId'] || changes['pmsDataId']) {
         this.cancelRiskResolutionClick();
         this.pendingQuickResolutionRowId = null;
         this.isRiskResolutionModalVisible = false;
         this.activeRiskResolutionRowId = null;

         this.isSupportingFilesModalVisible = false;
         this.activeSupportingFilesRowId = null;

         this.loadRiskResolutions();
         this.loadResolvedFinancialRisk();

         this.loadSupportingFileCounts();
      } else if (changes['reportData']) {
         // A refreshed generation can change attribution without changing its data ID.
         this.htmlReportResolvedFinancialRiskStore.refresh();
      }

      // 1) Rebuild definition/rows when report inputs change
      if (changes['reportType'] || changes['reportData']) {
         this.expandedCells.clear();
         this.expandedDetailRowIds.clear();

         this.definition = getReportDefinition(this.reportType);
         this.rebuildAnnotationDisabledLayoutIds();

         if (!this.definition) {
            this.persistedSupportingFileRowIds = new Set<string>();
            this.hasSupportingFileEligibleRows = false;
            this.annotations = EMPTY_HTML_REPORT_ANNOTATIONS;
            this.highlightByRowId.clear();
            this.commentByRowId.clear();
            this.checkedRowIds.clear();
            this.activeSortState = null;
            this.activeLayout = null;
            this.activeLayoutId = null;
            this.activeColumns = [];
            this.clearReportHeader();
            return;
         }

         const previousLayoutId = changes['reportType'] ? null : this.activeLayoutId;

         this.initializeActiveLayout(previousLayoutId);
         this.resetFilterState();
         this.initializeSortStateForActiveLayout();
         this.rebuildRowsForActiveLayout();
      }

      // 2) Load annotations when task changes OR when we toggle API usage
      if (
         changes['taskInstanceId'] ||
         changes['useApiAnnotations'] ||
         changes['seedAnnotations']
      ) {
         this.loadAnnotations();
      }

      if (
         changes['businessDate'] &&
         !changes['reportType'] &&
         !changes['reportData']
      ) {
         this.rebuildReportHeader();
         this.queueMentionTargetScroll();
      }

      if (
         !changes['reportType'] &&
         !changes['reportData'] &&
         (changes['taskInstanceId'] || changes['storageKey'])
      ) {
         this.loadCheckedRowsFromLocalStorage();
         this.cleanupOrphanCheckedRows();
      }
   }

   private initializeActiveLayout(
      preferredLayoutId: string | null | undefined
   ): void {
      if (!this.definition) {
         this.activeLayout = null;
         this.activeLayoutId = null;
         this.activeColumns = [];
         this.activeSortState = null;
         return;
      }

      const layouts = this.definition.layouts ?? [];

      if (!layouts.length) {
         this.activeLayout = null;
         this.activeLayoutId = null;
         this.activeColumns = this.definition.columns;
         return;
      }

      const storedLayoutId = this.getStoredSelectedLayoutId();

      const selectedLayout =
         layouts.find((layout) => layout.id === preferredLayoutId) ??
         layouts.find((layout) => layout.id === storedLayoutId) ??
         layouts[0];

      this.activeLayout = selectedLayout;
      this.activeLayoutId = selectedLayout.id;
      this.activeColumns = selectedLayout.columns;
   }

   private rebuildRowsForActiveLayout(): void {
      if (!this.definition) {
         this.hasRiskResolutionEligibleRows = false;
         this.hasSupportingFileEligibleRows = false;

         this.persistedSupportingFileRowIds = new Set<string>();

         this.allRows = [];
         this.visibleRows = [];
         this.activeColumns = [];
         this.clearReportHeader();
         return;
      }

      this.persistedSupportingFileRowIds = getPersistedPmsReportRowIds(
         this.reportData
      );

      const flattenRows =
         this.activeLayout?.flattenRows ?? this.definition.flattenRows;

      this.allRows = attachReportRowRisks(
         this.reportData ?? {},
         flattenRows(this.reportData ?? {})
      );

      this.hasRiskResolutionEligibleRows = this.allRows.some((row) =>
         isReportRowRiskResolutionEligible(row)
      );

      this.hasSupportingFileEligibleRows = this.allRows.some((row) =>
         isReportRowSupportingFileEligible(row, this.persistedSupportingFileRowIds)
      );

      this.applyRiskResolutionState();

      this.loadCheckedRowsFromLocalStorage();
      this.cleanupOrphanCheckedRows();
   }

   private applyRiskResolutionState(): void {
      if (!this.definition) {
         return;
      }

      if (this.riskResolutionLoadStatus !== 'LOADED') {
         this.allRows = clearReportRowRiskResolutions(this.allRows);
         this.rebuildVisibleRows();
         return;
      }

      this.allRows = attachReportRowRiskResolutions(
         this.allRows,
         this.riskResolutions
      );

      this.rebuildVisibleRows();
   }

   private resetFilterState(): void {
      this.filterCriteria = [];
      this.isFilterOpen = false;
   }

   private getActiveFilter() {
      return this.activeLayout?.filter ?? this.definition?.filter ?? null;
   }

   private getActiveSorting(): false | ReportSortingConfig | undefined {
      if (this.activeLayout?.sorting !== undefined) {
         return this.activeLayout.sorting;
      }

      return this.definition?.sorting;
   }

   private getNormalizedSortingConfig(): {
      enabled: boolean;
      persist: boolean;
      defaultSort: ReportSortState | null;
   } {
      const config = this.getActiveSorting();

      if (config === undefined || config === false || config.enabled === false) {
         return { enabled: false, persist: false, defaultSort: null };
      }

      return {
         enabled: true,
         persist: config.persist !== false,
         defaultSort: config.defaultSort ?? null
      };
   }

   private initializeSortStateForActiveLayout(): void {
      const sorting = this.getNormalizedSortingConfig();

      if (!sorting.enabled) {
         this.activeSortState = null;
         return;
      }

      const storedSort = sorting.persist
         ? this.loadSortStateFromLocalStorage()
         : null;
      const nextSortState = storedSort ?? sorting.defaultSort;

      if (nextSortState && this.isSortStateValid(nextSortState)) {
         this.activeSortState = nextSortState;
         return;
      }

      this.activeSortState = null;

      if (storedSort) {
         this.clearSortStateFromLocalStorage();
      }
   }

   private getSortStorageKey(): string | null {
      if (!this.definition?.reportType) return null;

      const definitionVersion = this.definition.version ?? 1;
      const layoutKey = this.activeLayoutId || 'default';

      return `tyalls.html-report-viewer.sort.v${definitionVersion}.${this.definition.reportType}.layout:${layoutKey}`;
   }

   private loadSortStateFromLocalStorage(): ReportSortState | null {
      const key = this.getSortStorageKey();
      if (!key) return null;

      try {
         const raw = window.localStorage.getItem(key);
         if (!raw) return null;

         const parsed = JSON.parse(raw) as Partial<ReportSortState>;
         const columnKey = String(parsed?.columnKey ?? '').trim();
         const direction = parsed?.direction;

         if (!columnKey || (direction !== 'asc' && direction !== 'desc')) {
            return null;
         }

         return { columnKey, direction };
      } catch (err) {
         console.warn('Unable to load HTML report sorting from localStorage', err);
         return null;
      }
   }

   private persistSortStateToLocalStorage(): void {
      const sorting = this.getNormalizedSortingConfig();
      if (!sorting.enabled || !sorting.persist) return;

      const key = this.getSortStorageKey();
      if (!key) return;

      try {
         if (!this.activeSortState) {
            window.localStorage.removeItem(key);
            return;
         }

         window.localStorage.setItem(key, JSON.stringify(this.activeSortState));
      } catch (err) {
         console.warn('Unable to persist HTML report sorting to localStorage', err);
      }
   }

   private clearSortStateFromLocalStorage(): void {
      const key = this.getSortStorageKey();
      if (!key) return;

      try {
         window.localStorage.removeItem(key);
      } catch (err) {
         console.warn('Unable to clear HTML report sorting from localStorage', err);
      }
   }

   private isSortStateValid(sortState: ReportSortState | null): boolean {
      if (!sortState) return false;

      const column = this.activeColumns.find(
         (item) => item.key === sortState.columnKey
      );

      return !!column && this.isColumnSortable(column);
   }

   private rebuildVisibleRows(): void {
      const filterFields = this.getFilterFields();
      const filteredRows = applyReportFilters(
         this.allRows,
         this.filterCriteria,
         filterFields
      );

      this.visibleRows = applyReportSorting(
         filteredRows,
         this.activeColumns,
         this.activeSortState
      );

      this.rebuildReportHeader();
      this.queueMentionTargetScroll();
   }

   private queueMentionTargetScroll(): void {
      const targetRowId = this.targetRowId?.trim();

      if (
         !targetRowId ||
         this.hasScrolledToMentionTarget ||
         !this.visibleRows.some((row) => row.rowId?.trim() === targetRowId)
      ) {
         return;
      }

      requestAnimationFrame(() => {
         const rowElements =
            this.elementRef.nativeElement.querySelectorAll<HTMLTableRowElement>(
               'tr[data-report-row-id]'
            );

         const targetElement = Array.from(rowElements).find(
            (element) => element.dataset['reportRowId'] === targetRowId
         );

         if (!targetElement) {
            return;
         }

         targetElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest'
         });

         this.hasScrolledToMentionTarget = true;
      });
   }

   private rebuildAnnotationDisabledLayoutIds(): void {
      const layouts = this.definition?.layouts ?? [];

      this.annotationDisabledLayoutIds = new Set(
         layouts
            .filter((layout) => !this.areLayoutAnnotationsEnabled(layout))
            .map((layout) => layout.id)
      );
   }

   private areLayoutAnnotationsEnabled(layout: ReportLayoutDefinition): boolean {
      const config = layout.annotations ?? this.definition?.annotations;

      if (typeof config === 'boolean') {
         return config;
      }

      return config?.enabled === true;
   }

   getRowClass(row: ReportRowVm): string {
      const baseClass = String(row?.rowClass ?? '').trim();

      const manualHighlightClass = this.highlightByRowId.has(row?.rowId)
         ? 'row-manual-highlight'
         : '';

      const mentionTargetClass =
         !!this.targetRowId?.trim() && row?.rowId?.trim() === this.targetRowId.trim()
            ? 'row-mention-target'
            : '';

      if (!row?.risk?.hasRisk) {
         return [baseClass, manualHighlightClass, mentionTargetClass]
            .filter(Boolean)
            .join(' ');
      }

      const severityClass = getReportRowRiskSeverityClass(row.risk.severity);

      const riskClass = severityClass
         ? `row-risk row-risk-${severityClass}`
         : 'row-risk row-risk-unclassified';

      return [baseClass, manualHighlightClass, riskClass, mentionTargetClass]
         .filter(Boolean)
         .join(' ');
   }

   get activeRiskResolutionRow(): ReportRowVm | null {
      if (!this.activeRiskResolutionRowId) {
         return null;
      }

      return (
         this.allRows.find((row) => row.rowId === this.activeRiskResolutionRowId) ??
         null
      );
   }

   isRiskResolutionEligible(row: ReportRowVm): boolean {
      return isReportRowRiskResolutionEligible(row);
   }

   isSupportingFilesEligible(row: ReportRowVm): boolean {
      return isReportRowSupportingFileEligible(
         row,
         this.persistedSupportingFileRowIds
      );
   }

   isRowMentionEligible(rowId: string | null): boolean {
      const normalizedRowId = rowId?.trim();

      return (
         !!normalizedRowId && this.persistedSupportingFileRowIds.has(normalizedRowId)
      );
   }

   getSupportingFileCount(row: ReportRowVm): number {
      return this.supportingFileCountsByRowId.get(row.rowId.trim()) ?? 0;
   }

   getRowCommentCount(row: ReportRowVm): number {
      return this.rowActivityCountsByRowId.get(row.rowId.trim())?.commentCount ?? 0;
   }

   getRowMentionCount(row: ReportRowVm): number {
      return this.rowActivityCountsByRowId.get(row.rowId.trim())?.mentionCount ?? 0;
   }

   hasRowCommentActivity(row: ReportRowVm): boolean {
      return this.getRowCommentCount(row) > 0 || this.getRowMentionCount(row) > 0;
   }

   getCommentActivityTooltip(row: ReportRowVm): string {
      const commentCount = this.getRowCommentCount(row);
      const mentionCount = this.getRowMentionCount(row);

      if (commentCount === 0 && mentionCount === 0) {
         return this.getCommentTooltip(row.rowId);
      }

      const summary =
         `${commentCount} comment${commentCount === 1 ? '' : 's'}, ` +
         `${mentionCount} mention${mentionCount === 1 ? '' : 's'}`;

      const existingTooltip = this.getCommentTooltip(row.rowId);

      return existingTooltip === 'Add comment'
         ? summary
         : `${summary} — ${existingTooltip}`;
   }

   getSupportingFilesTooltip(row: ReportRowVm): string {
      const count = this.getSupportingFileCount(row);

      if (count === 0) {
         return 'Supporting documents';
      }

      return `${count} supporting document${count === 1 ? '' : 's'}`;
   }

   isRiskResolutionButtonDisabled(row: ReportRowVm): boolean {
      if (!this.isRiskResolutionEligible(row)) {
         return true;
      }

      if (this.riskResolutionMutationStatus === 'SAVING') {
         return true;
      }

      if (
         this.riskResolutionLoadStatus === 'IDLE' ||
         this.riskResolutionLoadStatus === 'LOADING'
      ) {
         return true;
      }

      if (this.riskResolutionLoadStatus === 'ERROR') {
         return false;
      }

      return !row.riskResolution;
   }

   getRiskResolutionIconClass(row: ReportRowVm): string {
      if (this.riskResolutionLoadStatus === 'LOADING') {
         return 'pi pi-spinner pi-spin';
      }

      if (this.riskResolutionLoadStatus === 'ERROR') {
         return 'pi pi-exclamation-circle';
      }

      switch (row.riskResolution?.status) {
         case 'RESOLVED':
            return 'pi pi-check-circle';

         case 'PARTIALLY_RESOLVED':
            return 'pi pi-minus-circle';

         default:
            return 'pi pi-circle';
      }
   }

   getRiskResolutionButtonClass(row: ReportRowVm): string {
      if (this.riskResolutionLoadStatus === 'LOADING') {
         return 'risk-resolution-button is-loading';
      }

      if (this.riskResolutionLoadStatus === 'ERROR') {
         return 'risk-resolution-button is-error';
      }

      switch (row.riskResolution?.status) {
         case 'RESOLVED':
            return 'risk-resolution-button is-resolved';

         case 'PARTIALLY_RESOLVED':
            return 'risk-resolution-button is-partial';

         default:
            return 'risk-resolution-button is-unresolved';
      }
   }

   getRiskResolutionTooltip(row: ReportRowVm): string {
      if (this.riskResolutionLoadStatus === 'LOADING') {
         return 'Loading risk resolution state';
      }

      if (this.riskResolutionLoadStatus === 'ERROR') {
         return 'Unable to load risk resolution state. Click to retry.';
      }

      switch (row.riskResolution?.status) {
         case 'RESOLVED':
            return 'Resolved — click to unresolve all; double-click or Shift+click to select risks';

         case 'PARTIALLY_RESOLVED':
            return `${row.riskResolution.resolvedRiskCount} of ${row.riskResolution.totalRiskCount} risks resolved — click to resolve remaining; double-click or Shift+click to select risks`;

         default:
            return 'Unresolved — click to resolve all; double-click or Shift+click to select risks';
      }
   }

   onRiskResolutionButtonClick(row: ReportRowVm, event: MouseEvent): void {
      event.preventDefault();
      event.stopPropagation();

      if (!this.isRiskResolutionEligible(row)) {
         return;
      }

      if (this.riskResolutionLoadStatus === 'ERROR') {
         this.htmlReportRiskResolutionStore.refresh();
         return;
      }

      if (
         this.riskResolutionLoadStatus !== 'LOADED' ||
         this.riskResolutionMutationStatus === 'SAVING' ||
         !row.riskResolution
      ) {
         return;
      }

      // Shift+click (including Shift+Enter) makes selective resolution accessible
      // without requiring a mouse double-click.
      if (event.shiftKey) {
         this.cancelRiskResolutionClick();
         this.openRiskResolutionModal(row.rowId);
         return;
      }

      // Keyboard-generated clicks have detail=0. They are unambiguous single
      // actions, so there is no need to delay them for double-click detection.
      if (event.detail === 0) {
         this.cancelRiskResolutionClick();
         this.quickToggleRiskResolution(row.rowId);
         return;
      }

      if (this.riskResolutionClickTimer !== null) {
         this.cancelRiskResolutionClick();
      }

      const rowId = row.rowId;
      this.riskResolutionClickTimer = setTimeout(() => {
         this.riskResolutionClickTimer = null;
         this.quickToggleRiskResolution(rowId);
      }, 250);
   }

   onRiskResolutionButtonDoubleClick(row: ReportRowVm, event: MouseEvent): void {
      event.preventDefault();
      event.stopPropagation();
      this.cancelRiskResolutionClick();

      if (
         this.riskResolutionLoadStatus === 'LOADED' &&
         this.riskResolutionMutationStatus !== 'SAVING' &&
         this.isRiskResolutionEligible(row)
      ) {
         this.openRiskResolutionModal(row.rowId);
      }
   }

   private cancelRiskResolutionClick(): void {
      if (this.riskResolutionClickTimer !== null) {
         clearTimeout(this.riskResolutionClickTimer);
         this.riskResolutionClickTimer = null;
      }
   }

   private openRiskResolutionModal(rowId: string): void {
      this.activeRiskResolutionRowId = rowId;
      this.isRiskResolutionModalVisible = true;
   }

   private quickToggleRiskResolution(rowId: string): void {
      const row = this.allRows.find((item) => item.rowId === rowId);
      if (
         !row ||
         !this.isRiskResolutionEligible(row) ||
         !row.riskResolution ||
         !this.pmsDataId ||
         this.riskResolutionLoadStatus !== 'LOADED' ||
         this.riskResolutionMutationStatus === 'SAVING' ||
         this.isRiskResolutionModalVisible
      ) {
         return;
      }

      const action =
         row.riskResolution.status === 'RESOLVED' ? 'UNRESOLVE' : 'RESOLVE';
      const riskCodes = row.riskResolution.risks
         .filter((risk) =>
            action === 'RESOLVE' ? !risk.isResolved : risk.isResolved
         )
         .map((risk) => risk.riskCode);

      if (!riskCodes.length) {
         return;
      }

      this.pendingQuickResolutionRowId = rowId;
      this.htmlReportRiskResolutionStore.change({
         pmsDataId: this.pmsDataId,
         rowId,
         riskCodes,
         action,
         comment: null
      });
   }

   onRiskResolutionModalVisibleChange(visible: boolean): void {
      this.isRiskResolutionModalVisible = visible;

      if (!visible) {
         this.activeRiskResolutionRowId = null;
      }
   }

   onRiskResolutionChanged(event: ReportRowRiskResolutionChangedEvent): void {
      const action = event.response.action === 'RESOLVE' ? 'resolved' : 'unresolved';

      const riskLabel = event.response.affectedRiskCount === 1 ? 'risk' : 'risks';

      this.messageService.add({
         severity: 'success',
         summary: 'Risk resolution updated',
         detail: `${event.response.affectedRiskCount} ${riskLabel} ${action}.`
      });
   }

   getRowRiskTooltip(row: ReportRowVm): string | null {
      const risk = row?.risk;

      if (!risk?.hasRisk) {
         return null;
      }

      const severityLabel = risk.severity ? ` (${risk.severity})` : '';
      const details = risk.messages.length ? risk.messages : risk.riskCodes;

      return details.length
         ? `Risk${severityLabel}: ${details.join(' | ')}`
         : `Risk${severityLabel}`;
   }

   isColumnSortable(col: ReportColumn): boolean {
      const sorting = this.getNormalizedSortingConfig();

      return sorting.enabled && col.sort !== undefined && col.sort !== false;
   }

   isColumnSorted(col: ReportColumn): boolean {
      return this.activeSortState?.columnKey === col.key;
   }

   getColumnAriaSort(col: ReportColumn): 'ascending' | 'descending' | 'none' {
      if (!this.isColumnSorted(col)) return 'none';

      return this.activeSortState?.direction === 'desc' ? 'descending' : 'ascending';
   }

   getSortIconClass(col: ReportColumn): string {
      if (!this.isColumnSorted(col)) return 'pi pi-sort-alt sort-icon-muted';

      return this.activeSortState?.direction === 'desc'
         ? 'pi pi-sort-amount-down-alt'
         : 'pi pi-sort-amount-up-alt';
   }

   onReportHeaderActionRequested(): void {
      this.reportHeaderActionRequested.emit();
   }

   onSortColumn(col: ReportColumn): void {
      const columnSort = col.sort;

      if (
         !this.isColumnSortable(col) ||
         columnSort === undefined ||
         columnSort === false
      ) {
         return;
      }

      const current = this.activeSortState;
      const nextDirection =
         current?.columnKey === col.key
            ? current.direction === 'asc'
               ? 'desc'
               : 'asc'
            : (columnSort.defaultDirection ?? 'asc');

      this.activeSortState = {
         columnKey: col.key,
         direction: nextDirection
      };

      this.persistSortStateToLocalStorage();
      this.rebuildVisibleRows();
   }

   private getActiveHeader(): ReportHeaderConfig | undefined {
      return this.activeLayout?.header ?? this.definition?.header;
   }

   private getActiveRowChecks(): ReportRowCheckConfig | undefined {
      return this.activeLayout?.rowChecks ?? this.definition?.rowChecks;
   }

   private getActiveRowAnnotations(): ReportRowAnnotationConfig | undefined {
      return this.activeLayout?.annotations ?? this.definition?.annotations;
   }

   private getActiveRowDetails(): ReportRowDetailsConfig | undefined {
      return this.activeLayout?.rowDetails ?? this.definition?.rowDetails;
   }

   private getSelectedLayoutStorageKey(): string | null {
      if (!this.definition?.reportType) return null;

      return `tyalls.html-report-viewer.selected-layout.${this.definition.reportType}`;
   }

   private getStoredSelectedLayoutId(): string | null {
      const key = this.getSelectedLayoutStorageKey();
      if (!key) return null;

      try {
         return window.localStorage.getItem(key);
      } catch (err) {
         console.warn(
            'Unable to load selected HTML report layout from localStorage',
            err
         );
         return null;
      }
   }

   private persistSelectedLayout(): void {
      const key = this.getSelectedLayoutStorageKey();
      if (!key || !this.activeLayoutId) return;

      try {
         window.localStorage.setItem(key, this.activeLayoutId);
      } catch (err) {
         console.warn(
            'Unable to persist selected HTML report layout to localStorage',
            err
         );
      }
   }

   onRowDoubleClick(row: ReportRowVm): void {
      if (!this.taskInstanceId || !this.isRowAnnotatable(row)) return;

      const rowId = row.rowId;
      if (this.pendingRowOps.has(rowId)) return;

      const wasHighlighted = this.highlightByRowId.has(rowId);

      // optimistic UI update
      const previous = this.highlightByRowId.get(rowId) ?? null;
      if (wasHighlighted) this.highlightByRowId.delete(rowId);
      else this.highlightByRowId.set(rowId, { color: this.defaultHighlightColor });

      this.rebuildAnnotationsFromMaps();

      this.pendingRowOps.add(rowId);

      this.taskService
         .upsertHtmlReportAnnotation(this.taskInstanceId, {
            rowId,
            kind: 'HIGHLIGHT',
            color: wasHighlighted ? null : this.defaultHighlightColor,
            isRemove: wasHighlighted
         })
         .subscribe({
            next: () => {
               this.pendingRowOps.delete(rowId);
            },
            error: (err) => {
               this.pendingRowOps.delete(rowId);

               // revert UI
               if (previous) this.highlightByRowId.set(rowId, previous);
               else this.highlightByRowId.delete(rowId);

               this.rebuildAnnotationsFromMaps();
               this.notifyError('Unable to update highlight', err);
            }
         });
   }

   openComment(row: ReportRowVm): void {
      if (!this.taskInstanceId || !this.isRowAnnotatable(row)) return;

      this.activeCommentRowId = row.rowId;
      this.isCommentModalVisible = true;
   }

   openSupportingFiles(row: ReportRowVm, event: MouseEvent): void {
      event.preventDefault();
      event.stopPropagation();

      if (
         !this.taskInstanceId ||
         !this.pmsDataId ||
         !this.isSupportingFilesEligible(row)
      ) {
         return;
      }

      this.activeSupportingFilesRowId = row.rowId.trim();
      this.isSupportingFilesModalVisible = true;
   }

   openSupportingDocumentViewer(row: ReportRowVm, event: MouseEvent): void {
      event.preventDefault();
      event.stopPropagation();

      if (
         !this.taskInstanceId ||
         !this.pmsDataId ||
         !this.isSupportingFilesEligible(row)
      ) {
         return;
      }

      const url = this.router.serializeUrl(
         this.router.createUrlTree([
            '/pms-row-documents',
            this.taskInstanceId,
            this.pmsDataId,
            row.rowId.trim()
         ])
      );

      const viewerWindow = window.open(
         url,
         'tyalls-pms-row-document-viewer',
         'popup=yes,width=1400,height=900,resizable=yes,scrollbars=yes'
      );

      if (!viewerWindow) {
         this.messageService.add({
            severity: 'warn',
            summary: 'Document viewer blocked',
            detail: 'Please allow popups for Tyalls to open supporting documents.'
         });
         return;
      }

      viewerWindow.focus();
   }

   onSupportingFilesModalVisibleChange(visible: boolean): void {
      this.isSupportingFilesModalVisible = visible;

      if (!visible) {
         this.activeSupportingFilesRowId = null;
      }
   }

   onSupportingFileCountChanged(event: {
      taskInstanceId: number;
      pmsDataId: number;
      targetRowId: string;
      fileCount: number;
   }): void {
      this.htmlReportSupportingFileCountStore.setRowCount(
         event.taskInstanceId,
         event.pmsDataId,
         event.targetRowId,
         event.fileCount
      );
   }

   onCommentModalVisibleChange(visible: boolean): void {
      if (visible) {
         this.isCommentModalVisible = true;
         return;
      }

      this.closeCommentModal();
   }

   onRowCommentAdded(event: ReportRowCommentAddedEvent): void {
      if (!this.isRowIdAnnotatable(event.rowId)) return;

      this.commentByRowId.set(event.rowId, {
         text: event.comment.commentText,
         updatedAtIso: event.comment.createdAtIso,
         mentionsJson: '[]',
         followUpDateIso: null
      });

      if (this.taskInstanceId) {
         this.htmlReportRowActivityCountStore.recordCommentAdded(
            this.taskInstanceId,
            event.rowId,
            event.comment.mentions?.length ?? 0
         );
      }

      this.rebuildAnnotationsFromMaps();

      // Row mentions are committed with the comment. Invalidate only the
      // Mentions store after a successful save; ordinary comments need no fetch.
      if (event.comment.mentions?.length) {
         this.mentionStore.refreshStore();
      }
   }

   closeCommentModal(): void {
      this.isCommentModalVisible = false;
      this.activeCommentRowId = null;
   }

   getCommentTooltip(rowId: string): string {
      const raw = this.commentByRowId.get(rowId)?.text?.trim();
      if (!raw) return 'Add comment';
      // Tooltips don’t handle newlines nicely — keep it single-line
      const singleLine = raw.replace(/\s+/g, ' ').trim();
      return singleLine.length > 160 ? singleLine.slice(0, 160) + '…' : singleLine;
   }

   private notifyError(summary: string, err: any): void {
      console.error(summary, err);

      this.messageService.add({
         severity: 'error',
         summary,
         detail: 'Please try again. If the issue persists, contact support.'
      });
   }

   hasComment(rowId: string): boolean {
      if (!this.isRowIdAnnotatable(rowId)) return false;

      return !!this.commentByRowId.get(rowId)?.text?.trim();
   }

   getHighlightColor(rowId: string): string | null {
      if (!this.isRowIdAnnotatable(rowId)) return null;

      return this.highlightByRowId.get(rowId)?.color ?? null;
   }

   getCommentIconClass(rowId: string): string {
      return this.hasComment(rowId) ? 'comment-icon has-comment' : 'comment-icon';
   }

   toggleFilterPanel(): void {
      this.isFilterOpen = !this.isFilterOpen;
   }

   hasMultipleLayouts(): boolean {
      return (this.definition?.layouts?.length ?? 0) > 1;
   }

   getActiveTableMinWidth(): string | null {
      return (
         this.activeLayout?.tableMinWidth ?? this.definition?.tableMinWidth ?? null
      );
   }

   isRowDetailsEnabled(): boolean {
      const config = this.getActiveRowDetails();

      return config?.enabled === true && (config.fields?.length ?? 0) > 0;
   }

   isRowDetailsAvailable(row: ReportRowVm): boolean {
      const config = this.getActiveRowDetails();

      if (!config?.enabled || !config.fields?.length) {
         return false;
      }

      if (!config.selectableRowTypes?.length) {
         return true;
      }

      const rowType = String(row.cells?.['rowType'] ?? '');

      return config.selectableRowTypes.includes(rowType);
   }

   isRowDetailsExpanded(row: ReportRowVm): boolean {
      return (
         this.isRowDetailsAvailable(row) && this.expandedDetailRowIds.has(row.rowId)
      );
   }

   toggleRowDetails(row: ReportRowVm, event: MouseEvent): void {
      event.preventDefault();
      event.stopPropagation();

      if (!this.isRowDetailsAvailable(row)) {
         return;
      }

      if (this.expandedDetailRowIds.has(row.rowId)) {
         this.expandedDetailRowIds.delete(row.rowId);
         return;
      }

      this.expandedDetailRowIds.add(row.rowId);
   }

   getActiveRowDetailFields(): ReportRowDetailField[] {
      return this.getActiveRowDetails()?.fields ?? [];
   }

   getVisibleRowDetailFields(row: ReportRowVm): ReportRowDetailField[] {
      return this.getActiveRowDetailFields().filter(
         (field) => field.visible?.(row) !== false
      );
   }

   getRowDetailValue(row: ReportRowVm, field: ReportRowDetailField): string {
      const value = row.cells?.[field.key];

      if (value === null || value === undefined || value === '') {
         return '—';
      }

      return String(value);
   }

   getLeadingColumnSpan(row: ReportRowVm): number {
      const rawSpan = Number(row.cells?.['leadingColumnSpan']);

      if (!Number.isInteger(rawSpan) || rawSpan < 1) {
         return 1;
      }

      return Math.min(rawSpan, this.activeColumns.length);
   }

   shouldRenderReportCell(row: ReportRowVm, columnIndex: number): boolean {
      const leadingColumnSpan = this.getLeadingColumnSpan(row);

      return columnIndex === 0 || columnIndex >= leadingColumnSpan;
   }

   getTableColumnCount(): number {
      return (
         1 +
         (this.isRowDetailsEnabled() ? 1 : 0) +
         (this.isRowChecksEnabled() ? 1 : 0) +
         this.activeColumns.length +
         (this.hasRiskResolutionEligibleRows ? 1 : 0) +
         (this.hasSupportingFileEligibleRows ? 1 : 0) +
         (this.areRowAnnotationsEnabled() ? 1 : 0)
      );
   }

   selectLayout(layoutId: string): void {
      if (!this.definition || layoutId === this.activeLayoutId) return;

      const layout = this.definition.layouts?.find((item) => item.id === layoutId);
      if (!layout) return;

      this.activeLayout = layout;
      this.activeLayoutId = layout.id;
      this.activeColumns = layout.columns;

      this.expandedCells.clear();
      this.expandedDetailRowIds.clear();
      this.checkedRowIds.clear();

      this.resetFilterState();
      this.persistSelectedLayout();
      this.initializeSortStateForActiveLayout();
      this.rebuildRowsForActiveLayout();

      // Re-load annotations because row IDs may differ by layout.
      this.loadAnnotations();
   }

   getFilterFields(): FilterFieldDef[] {
      return this.getActiveFilter()?.fields ?? [];
   }

   get activeFilterCount(): number {
      return this.filterCriteria.filter((criterion) =>
         this.isActiveFilterCriterion(criterion)
      ).length;
   }

   private isActiveFilterCriterion(criterion: FilterCriterion): boolean {
      if (!criterion.fieldKey || !criterion.operator) {
         return false;
      }

      if (criterion.operator === 'isEmpty' || criterion.operator === 'isNotEmpty') {
         return true;
      }

      if (criterion.operator === 'between') {
         return (
            this.hasFilterValue(criterion.value) &&
            this.hasFilterValue(criterion.valueTo)
         );
      }

      return this.hasFilterValue(criterion.value);
   }

   private hasFilterValue(value: any): boolean {
      if (value === null || value === undefined) {
         return false;
      }

      if (value instanceof Date) {
         return !Number.isNaN(value.getTime());
      }

      return String(value).trim() !== '';
   }

   addFirstFilterIfEmpty(): void {
      if (this.filterCriteria.length) return;
      this.filterCriteria = [this.createEmptyCriterion()];
   }

   addFilterAfter(index: number): void {
      const next: FilterCriterion = {
         id: this.newId(),
         fieldKey: null,
         operator: null,
         value: null,
         valueTo: null,
         joinWithPrev: 'AND'
      };
      this.filterCriteria = [
         ...this.filterCriteria.slice(0, index + 1),
         next,
         ...this.filterCriteria.slice(index + 1)
      ];
   }

   removeFilter(index: number): void {
      const copy = [...this.filterCriteria];
      copy.splice(index, 1);

      // ensure first row has no joinWithPrev
      if (copy[0]) delete copy[0].joinWithPrev;

      this.filterCriteria = copy;
      this.applyFilters(); // optional auto-apply
   }

   clearFilters(): void {
      this.filterCriteria = [this.createEmptyCriterion()];
      this.rebuildVisibleRows();
   }

   applyFilters(): void {
      this.rebuildVisibleRows();
   }

   private createEmptyCriterion(): FilterCriterion {
      return {
         id: this.newId(),
         fieldKey: null,
         operator: null,
         value: null,
         valueTo: null
      };
   }

   hasActiveSort(): boolean {
      return this.activeSortState !== null;
   }

   clearSort(): void {
      this.activeSortState = null;
      this.clearSortStateFromLocalStorage();
      this.rebuildVisibleRows();
   }

   onFieldChanged(c: FilterCriterion): void {
      c.operator = null;
      c.value = null;
      c.valueTo = null;
   }

   onOperatorChanged(c: FilterCriterion): void {
      // If operator is not BETWEEN, clear valueTo
      if (c.operator !== 'between') {
         c.valueTo = null;
      }

      // Operators like isEmpty/isNotEmpty don't need a value
      if (c.operator === 'isEmpty' || c.operator === 'isNotEmpty') {
         c.value = null;
         c.valueTo = null;
      }
   }

   getOperatorsForField(
      fieldKey: string | null
   ): { label: string; value: FilterOperator }[] {
      const f = this.getFilterFields().find((x) => x.key === fieldKey);
      if (!f) return [];

      if (f.type === 'string') {
         return [
            { label: 'Equals', value: 'eq' },
            { label: 'Like', value: 'contains' },
            { label: 'Starts With', value: 'startsWith' },
            { label: 'Ends With', value: 'endsWith' },
            { label: 'Is Empty', value: 'isEmpty' },
            { label: 'Is Not Empty', value: 'isNotEmpty' }
         ];
      }

      // number/date
      return [
         { label: 'Equals', value: 'eq' },
         { label: 'Not Equals', value: 'neq' },
         { label: 'Greater Than', value: 'gt' },
         { label: 'Greater Than / Equal', value: 'gte' },
         { label: 'Less Than', value: 'lt' },
         { label: 'Less Than / Equal', value: 'lte' },
         { label: 'Between', value: 'between' },
         { label: 'Is Empty', value: 'isEmpty' },
         { label: 'Is Not Empty', value: 'isNotEmpty' }
      ];
   }

   isBetween(op: FilterOperator | null): boolean {
      return op === 'between';
   }

   getFieldType(fieldKey: string | null): 'string' | 'number' | 'date' {
      return this.getFilterFields().find((f) => f.key === fieldKey)?.type ?? 'string';
   }

   isReportHeaderVisible(): boolean {
      return this.getActiveHeader()?.show !== false;
   }

   private clearReportHeader(): void {
      this.reportHeaderTitle = '';
      this.reportHeaderBusinessDateLabel = 'Business Date';
      this.reportHeaderBusinessDateText = '';
      this.reportHeaderSnippets = [];
   }

   private rebuildReportHeader(): void {
      if (!this.definition) {
         this.clearReportHeader();
         return;
      }

      const header = this.getActiveHeader();
      const context = this.createReportHeaderContext();

      this.reportHeaderTitle = this.resolveHeaderText(
         header?.title,
         this.definition.title,
         context
      );

      this.reportHeaderBusinessDateLabel =
         header?.businessDateLabel?.trim() || 'Business Date';

      const shouldShowBusinessDate = header?.showBusinessDate !== false;

      this.reportHeaderBusinessDateText = shouldShowBusinessDate
         ? this.formatBusinessDate(this.businessDate)
         : '';

      const standardSnippets = this.resolveStandardHeaderSnippets(this.reportData);
      const configuredSnippets = this.resolveHeaderSnippets(
         header?.snippets,
         context
      );

      this.reportHeaderSnippets = [...standardSnippets, ...configuredSnippets];
   }

   private createReportHeaderContext(): ReportHeaderContext {
      return {
         reportType: this.reportType,
         title: this.definition?.title ?? '',
         businessDate: this.businessDate,
         reportData: this.reportData,
         allRows: this.allRows,
         visibleRows: this.visibleRows,
         layoutId: this.activeLayoutId,
         layoutLabel: this.activeLayout?.label ?? null
      };
   }

   private resolveHeaderText(
      resolver: string | ((context: ReportHeaderContext) => string) | undefined,
      fallback: string,
      context: ReportHeaderContext
   ): string {
      if (!resolver) return fallback;

      if (typeof resolver === 'string') {
         return resolver.trim() || fallback;
      }

      try {
         return resolver(context)?.trim() || fallback;
      } catch (err) {
         console.warn('Unable to resolve report header title', err);
         return fallback;
      }
   }

   private resolveStandardHeaderSnippets(reportData: unknown): ReportHeaderSnippet[] {
      const reportMeta = getReportMeta(reportData);
      const riskSummary = getRiskSummary(reportData);
      const snippets: ReportHeaderSnippet[] = [];

      const riskEvaluationStatus =
         reportMeta?.RiskEvaluationStatus?.trim().toUpperCase() ?? '';

      const riskSnippetsUnavailable = [
         'SKIPPED',
         'FAILED',
         'FAILURE',
         'ERROR'
      ].includes(riskEvaluationStatus);

      const riskCountValue =
         this.getActiveHeader()?.riskCountSource === 'affectedRows'
            ? (riskSummary?.AffectedRowCount ??
              riskSummary?.RiskCount ??
              reportMeta?.RiskCount)
            : (riskSummary?.RiskCount ?? reportMeta?.RiskCount);
      const riskCount = this.getFiniteHeaderNumber(riskCountValue);

      if (!riskSnippetsUnavailable && riskCount !== null && riskCount > 0) {
         snippets.push({
            label: 'Risk Count',
            value: riskCount,
            tone: 'danger'
         });

         const totalRiskValue = this.getFiniteHeaderNumber(
            riskSummary?.TotalRiskValue
         );

         if (totalRiskValue !== null) {
            snippets.push({
               label: 'Total Risk Value',
               value: formatCurrency(totalRiskValue),
               tone: totalRiskValue !== 0 ? 'danger' : 'neutral'
            });
         }
      }

      // Financial value comes only from the authorized backend endpoint.
      // V1 winning-risk ownership is distinct from full-row resolution.
      if (
         this.taskInstanceId > 0 &&
         (this.pmsDataId ?? 0) > 0 &&
         !riskSnippetsUnavailable &&
         riskCount !== null &&
         riskCount > 0 &&
         this.getFiniteHeaderNumber(riskSummary?.TotalRiskValue) !== null
      ) {
         let resolvedValue: string | null = null;
         let resolvedTone: ReportHeaderSnippet['tone'] = 'neutral';

         if (this.resolvedFinancialRiskLoadStatus === 'LOADING') {
            resolvedValue = 'Loading...';
         } else if (this.resolvedFinancialRiskLoadStatus === 'ERROR') {
            resolvedValue = 'Unable to load';
            resolvedTone = 'warning';
         } else if (this.resolvedFinancialRiskLoadStatus === 'LOADED') {
            const result = this.resolvedFinancialRisk;
            const amount = this.getFiniteHeaderNumber(result?.totalResolvedRiskValue);

            if (
               result?.pmsDataId === this.pmsDataId &&
               result.isAvailable &&
               amount !== null
            ) {
               // Header monetary display is a positive magnitude, as agreed.
               resolvedValue = formatCurrency(Math.abs(amount));
               resolvedTone = amount !== 0 ? 'success' : 'neutral';
            } else {
               resolvedValue = 'Unavailable';
               resolvedTone = 'warning';
            }
         }

         if (resolvedValue !== null) {
            snippets.push({
               label: 'Total Resolved Risk Value',
               value: resolvedValue,
               tone: resolvedTone
            });
         }
      }

      const totalReportValue = this.getFiniteHeaderNumber(
         reportMeta?.TotalReportValue
      );

      if (totalReportValue !== null) {
         snippets.push({
            label: 'Total Report Value',
            value: formatCurrency(totalReportValue)
         });
      }

      return snippets;
   }

   private getFiniteHeaderNumber(value: unknown): number | null {
      return typeof value === 'number' && Number.isFinite(value) ? value : null;
   }

   private resolveHeaderSnippets(
      resolver:
         | ReportHeaderSnippet[]
         | ((context: ReportHeaderContext) => ReportHeaderSnippet[])
         | undefined,
      context: ReportHeaderContext
   ): ReportHeaderSnippet[] {
      if (!resolver) return [];

      try {
         const snippets =
            typeof resolver === 'function' ? resolver(context) : resolver;

         return (snippets ?? []).filter(
            (snippet) =>
               !!snippet &&
               !!String(snippet.label ?? '').trim() &&
               snippet.value !== null &&
               snippet.value !== undefined &&
               String(snippet.value).trim() !== ''
         );
      } catch (err) {
         console.warn('Unable to resolve report header snippets', err);
         return [];
      }
   }

   private formatBusinessDate(value: string | Date | null | undefined): string {
      if (!value) return '';

      if (value instanceof Date && !Number.isNaN(value.getTime())) {
         return this.formatDateParts(
            value.getDate(),
            value.getMonth() + 1,
            value.getFullYear()
         );
      }

      const raw = String(value).trim();
      if (!raw) return '';

      const isoDateMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (isoDateMatch) {
         return `${isoDateMatch[3]}/${isoDateMatch[2]}/${isoDateMatch[1]}`;
      }

      const shortIsoDateMatch = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
      if (shortIsoDateMatch) {
         return this.formatDateParts(
            Number(shortIsoDateMatch[3]),
            Number(shortIsoDateMatch[2]),
            Number(shortIsoDateMatch[1])
         );
      }

      return raw;
   }

   private formatDateParts(day: number, month: number, year: number): string {
      return `${this.padDatePart(day)}/${this.padDatePart(month)}/${year}`;
   }

   private padDatePart(value: number): string {
      return String(value).padStart(2, '0');
   }

   areRowAnnotationsEnabled(): boolean {
      return this.getNormalizedRowAnnotationConfig().enabled === true;
   }

   isRowAnnotatable(row: ReportRowVm): boolean {
      if (!this.areRowAnnotationsEnabled()) return false;
      if (!row?.rowId) return false;

      if (row.isAnnotatable === false) return false;
      if (row.isAnnotatable === true) return true;

      const config = this.getNormalizedRowAnnotationConfig();
      const rowType = String(row.cells?.['rowType'] ?? '');
      const rowClass = String(row.rowClass ?? '');
      const level = row.level ?? 0;

      if (level > 0 && !config.includeNestedRows) {
         return false;
      }

      if (config.selectableRowTypes?.length) {
         return config.selectableRowTypes.includes(rowType);
      }

      if (config.excludeRowTypes?.includes(rowType)) {
         return false;
      }

      if (this.defaultExcludedAnnotationRowTypes.has(rowType)) {
         return false;
      }

      if (
         rowClass.includes('report-group-header-row') ||
         rowClass.includes('report-group-total-row')
      ) {
         return false;
      }

      return true;
   }

   isPmsDataStatusBlockingReportRender(): boolean {
      const status = this.getNormalisedPmsDataStatus();

      return (
         status === PHOBOS_TASK_INSTANCE_PMS_DATA_STATUSES.PENDING_SOURCE ||
         status === PHOBOS_TASK_INSTANCE_PMS_DATA_STATUSES.READY_FOR_GENERATION ||
         status === PHOBOS_TASK_INSTANCE_PMS_DATA_STATUSES.NO_FINDINGS ||
         status === PHOBOS_TASK_INSTANCE_PMS_DATA_STATUSES.NO_SOURCE_DATA ||
         status === PHOBOS_TASK_INSTANCE_PMS_DATA_STATUSES.GENERATION_FAILED
      );
   }

   getPmsDataStatusMessageTitle(): string {
      const status = this.getNormalisedPmsDataStatus();

      switch (status) {
         case PHOBOS_TASK_INSTANCE_PMS_DATA_STATUSES.PENDING_SOURCE:
            return 'PMS data is waiting to be ingested';

         case PHOBOS_TASK_INSTANCE_PMS_DATA_STATUSES.READY_FOR_GENERATION:
            return 'Report data is being prepared';

         case PHOBOS_TASK_INSTANCE_PMS_DATA_STATUSES.NO_FINDINGS:
            return 'No findings found';

         case PHOBOS_TASK_INSTANCE_PMS_DATA_STATUSES.NO_SOURCE_DATA:
            return 'No PMS source data available';

         case PHOBOS_TASK_INSTANCE_PMS_DATA_STATUSES.GENERATION_FAILED:
            return 'Report generation failed';

         default:
            return 'Report is not available';
      }
   }

   getPmsDataStatusMessageBody(): string {
      const status = this.getNormalisedPmsDataStatus();
      const reportName = this.definition?.title || this.reportType || 'this report';

      switch (status) {
         case PHOBOS_TASK_INSTANCE_PMS_DATA_STATUSES.PENDING_SOURCE:
            return `The source PMS data for ${reportName} has not been ingested yet. Please check again shortly.`;

         case PHOBOS_TASK_INSTANCE_PMS_DATA_STATUSES.READY_FOR_GENERATION:
            return `The PMS source data is available and ${reportName} is waiting to be generated. Please check again shortly.`;

         case PHOBOS_TASK_INSTANCE_PMS_DATA_STATUSES.NO_FINDINGS:
            return `There are no findings for ${reportName} for this business date.`;

         case PHOBOS_TASK_INSTANCE_PMS_DATA_STATUSES.NO_SOURCE_DATA:
            return `No source PMS data was available for ${reportName}. This may happen if the PMS did not return data for this report/date.`;

         case PHOBOS_TASK_INSTANCE_PMS_DATA_STATUSES.GENERATION_FAILED:
            return `We could not generate ${reportName}. Please check the task comments or ask support to review the generation error.`;

         default:
            return `The report data is not currently available.`;
      }
   }

   getPmsDataStatusMessageClass(): string {
      const status = this.getNormalisedPmsDataStatus();

      switch (status) {
         case PHOBOS_TASK_INSTANCE_PMS_DATA_STATUSES.NO_FINDINGS:
            return 'status-no-findings';

         case PHOBOS_TASK_INSTANCE_PMS_DATA_STATUSES.NO_SOURCE_DATA:
            return 'status-no-source-data';

         case PHOBOS_TASK_INSTANCE_PMS_DATA_STATUSES.GENERATION_FAILED:
            return 'status-generation-failed';

         case PHOBOS_TASK_INSTANCE_PMS_DATA_STATUSES.PENDING_SOURCE:
         case PHOBOS_TASK_INSTANCE_PMS_DATA_STATUSES.READY_FOR_GENERATION:
            return 'status-pending';

         default:
            return 'status-neutral';
      }
   }

   private getNormalisedPmsDataStatus(): string | null {
      const status = this.pmsDataStatus?.trim();

      return status ? status.toUpperCase() : null;
   }

   private isRowIdAnnotatable(rowId: string): boolean {
      const row = this.allRows.find((item) => item.rowId === rowId);

      return !!row && this.isRowAnnotatable(row);
   }

   private getNormalizedRowAnnotationConfig(): {
      enabled: boolean;
      disabledMessage?: string;
      includeNestedRows?: boolean;
      selectableRowTypes?: string[];
      excludeRowTypes?: string[];
   } {
      const config = this.getActiveRowAnnotations();

      if (typeof config === 'boolean') {
         return { enabled: config };
      }

      return config ?? { enabled: false };
   }

   private clearRowAnnotationState(): void {
      this.annotations = EMPTY_HTML_REPORT_ANNOTATIONS;
      this.highlightByRowId.clear();
      this.commentByRowId.clear();
      this.pendingRowOps.clear();
      this.isAnnotationsLoading = false;
      this.closeCommentModal();
   }

   isRowChecksEnabled(): boolean {
      return this.getActiveRowChecks()?.enabled === true;
   }

   isRowCheckable(row: ReportRowVm): boolean {
      if (!this.isRowChecksEnabled()) return false;
      if (!row?.rowId) return false;

      if (row.isCheckable === false) return false;
      if (row.isCheckable === true) return true;

      const config = this.getActiveRowChecks();
      const rowType = String(row.cells?.['rowType'] ?? '');
      const rowClass = String(row.rowClass ?? '');
      const level = row.level ?? 0;

      if (level > 0 && !config?.includeNestedRows) {
         return false;
      }

      if (config?.selectableRowTypes?.length) {
         return config.selectableRowTypes.includes(rowType);
      }

      if (config?.excludeRowTypes?.includes(rowType)) {
         return false;
      }

      if (this.defaultExcludedCheckRowTypes.has(rowType)) {
         return false;
      }

      if (
         rowClass.includes('report-group-header-row') ||
         rowClass.includes('report-group-total-row')
      ) {
         return false;
      }

      return true;
   }

   isRowChecked(row: ReportRowVm): boolean {
      return this.checkedRowIds.has(row.rowId);
   }

   toggleRowChecked(row: ReportRowVm, event: Event): void {
      event.stopPropagation();

      if (!this.isRowCheckable(row)) return;

      const checked = (event.target as HTMLInputElement)?.checked === true;

      if (checked) {
         this.checkedRowIds.add(row.rowId);
      } else {
         this.checkedRowIds.delete(row.rowId);
      }

      this.persistCheckedRowsToLocalStorage();
   }

   getVisibleCheckableRows(): ReportRowVm[] {
      return this.visibleRows.filter((row) => this.isRowCheckable(row));
   }

   areAllVisibleCheckableRowsChecked(): boolean {
      const rows = this.getVisibleCheckableRows();
      return (
         rows.length > 0 && rows.every((row) => this.checkedRowIds.has(row.rowId))
      );
   }

   isSomeVisibleCheckableRowChecked(): boolean {
      const rows = this.getVisibleCheckableRows();
      return rows.some((row) => this.checkedRowIds.has(row.rowId));
   }

   toggleAllVisibleCheckedRows(event: Event): void {
      event.stopPropagation();

      const checked = (event.target as HTMLInputElement)?.checked === true;
      const rows = this.getVisibleCheckableRows();

      for (const row of rows) {
         if (checked) {
            this.checkedRowIds.add(row.rowId);
         } else {
            this.checkedRowIds.delete(row.rowId);
         }
      }

      this.persistCheckedRowsToLocalStorage();
   }

   private getRowChecksStorageKey(): string {
      const definitionVersion = this.definition?.version ?? 1;

      const reportInstanceKey =
         this.storageKey?.trim() ||
         `task:${this.taskInstanceId || 'unknown'}:report:${this.reportType || 'unknown'}`;

      const layoutKey = this.activeLayoutId || 'default';

      return `tyalls.html-report-viewer.row-checks.v${definitionVersion}.${reportInstanceKey}.layout:${layoutKey}`;
   }

   private loadCheckedRowsFromLocalStorage(): void {
      this.checkedRowIds.clear();

      if (!this.isRowChecksEnabled()) return;

      try {
         const raw = window.localStorage.getItem(this.getRowChecksStorageKey());
         if (!raw) return;

         const parsed = JSON.parse(raw);
         if (!Array.isArray(parsed)) return;

         this.checkedRowIds = new Set(parsed.map((rowId) => String(rowId)));
      } catch (err) {
         console.warn('Unable to load HTML report row checks from localStorage', err);
         this.checkedRowIds.clear();
      }
   }

   private persistCheckedRowsToLocalStorage(): void {
      if (!this.isRowChecksEnabled()) return;

      try {
         const key = this.getRowChecksStorageKey();
         const rowIds = Array.from(this.checkedRowIds).sort();

         if (!rowIds.length) {
            window.localStorage.removeItem(key);
            return;
         }

         window.localStorage.setItem(key, JSON.stringify(rowIds));
      } catch (err) {
         console.warn(
            'Unable to persist HTML report row checks to localStorage',
            err
         );
      }
   }

   private cleanupOrphanCheckedRows(): void {
      if (!this.isRowChecksEnabled()) return;

      const validRowIds = new Set(
         this.allRows
            .filter((row) => this.isRowCheckable(row))
            .map((row) => row.rowId)
      );

      let changed = false;

      for (const rowId of Array.from(this.checkedRowIds)) {
         if (!validRowIds.has(rowId)) {
            this.checkedRowIds.delete(rowId);
            changed = true;
         }
      }

      if (changed) {
         this.persistCheckedRowsToLocalStorage();
      }
   }

   trackByRowId(_: number, row: ReportRowVm): string {
      return row.rowId;
   }

   // =======================
   // ADD HERE: Cell truncation / expansion helpers
   // =======================
   private cellKey(rowId: string, colKey: string): string {
      return `${rowId}::${colKey}`;
   }

   getColWidth(col: ReportColumn): string {
      return col.width ?? this.defaultColumnWidth;
   }

   getMaxChars(col: ReportColumn): number {
      return col.maxChars ?? this.defaultCellCharLimit;
   }

   getCellText(row: ReportRowVm, col: ReportColumn): string {
      const v = row?.cells?.[col.key];
      return v === null || v === undefined ? '' : String(v);
   }

   isCellExpanded(rowId: string, colKey: string): boolean {
      return this.expandedCells.has(this.cellKey(rowId, colKey));
   }

   isTruncatable(row: ReportRowVm, col: ReportColumn): boolean {
      const text = this.getCellText(row, col);
      return text.length > this.getMaxChars(col);
   }

   getDisplayText(row: ReportRowVm, col: ReportColumn): string {
      const text = this.getCellText(row, col);

      if (this.isCellExpanded(row.rowId, col.key)) return text;

      const limit = this.getMaxChars(col);
      return text.length > limit ? text.slice(0, limit) : text;
   }

   toggleCell(rowId: string, colKey: string, ev: MouseEvent): void {
      ev.preventDefault();
      ev.stopPropagation(); // prevents triggering row dblclick highlight

      const key = this.cellKey(rowId, colKey);
      if (this.expandedCells.has(key)) this.expandedCells.delete(key);
      else this.expandedCells.add(key);
   }

   private newId(): string {
      return Math.random().toString(36).slice(2, 10);
   }

   private cleanupOrphanAnnotations(): void {
      const validRowIds = new Set(this.allRows.map((r) => r.rowId)); // IMPORTANT
      this.annotations = {
         rows: (this.annotations.rows ?? []).filter((r) => validRowIds.has(r.rowId))
      };
   }

   private rebuildMapsFromAnnotations(): void {
      this.highlightByRowId.clear();
      this.commentByRowId.clear();

      for (const r of this.annotations.rows ?? []) {
         if (!r?.rowId) continue;
         if (r.highlight) this.highlightByRowId.set(r.rowId, r.highlight);
         if (r.comment) this.commentByRowId.set(r.rowId, r.comment);
      }
   }

   private rebuildAnnotationsFromMaps(): void {
      if (!this.areRowAnnotationsEnabled()) {
         this.clearRowAnnotationState();
         return;
      }

      const rowIds = new Set<string>([
         ...this.highlightByRowId.keys(),
         ...this.commentByRowId.keys()
      ]);

      this.annotations = {
         rows: Array.from(rowIds).map((rowId) => ({
            rowId,
            highlight: this.highlightByRowId.get(rowId) ?? null,
            comment: this.commentByRowId.get(rowId) ?? null
         }))
      };
   }

   private loadRiskResolutions(): void {
      const taskInstanceId = this.taskInstanceId;
      const pmsDataId = this.pmsDataId;

      if (!taskInstanceId || !pmsDataId) {
         this.htmlReportRiskResolutionStore.reset();
         return;
      }

      this.htmlReportRiskResolutionStore.load(taskInstanceId, pmsDataId);
   }

   private loadResolvedFinancialRisk(): void {
      const taskInstanceId = this.taskInstanceId;
      const pmsDataId = this.pmsDataId;

      if (!taskInstanceId || !pmsDataId) {
         this.htmlReportResolvedFinancialRiskStore.reset();
         return;
      }

      this.htmlReportResolvedFinancialRiskStore.load(taskInstanceId, pmsDataId);
   }

   private loadSupportingFileCounts(): void {
      const taskInstanceId = this.taskInstanceId;
      const pmsDataId = this.pmsDataId;

      if (!taskInstanceId || !pmsDataId) {
         this.htmlReportSupportingFileCountStore.reset();
         return;
      }

      this.htmlReportSupportingFileCountStore.load(taskInstanceId, pmsDataId);
   }

   private loadRowActivityCounts(): void {
      const taskInstanceId = this.taskInstanceId;

      if (!taskInstanceId) {
         this.htmlReportRowActivityCountStore.reset();
         return;
      }

      this.htmlReportRowActivityCountStore.load(taskInstanceId);
   }
   private loadAnnotations(): void {
      if (this.isPmsDataStatusBlockingReportRender()) {
         this.clearRowAnnotationState();
         return;
      }

      if (!this.areRowAnnotationsEnabled()) {
         this.clearRowAnnotationState();
         return;
      }

      // If we have no rows yet, wait until report is ready
      if (!this.definition || !this.allRows?.length) return;

      // If API is disabled or taskInstanceId missing, fall back to seed
      if (!this.useApiAnnotations || !this.taskInstanceId) {
         this.annotations = this.normalizeAnnotations(this.seedAnnotations);
         this.cleanupOrphanAnnotations();
         this.rebuildMapsFromAnnotations();
         return;
      }

      this.isAnnotationsLoading = true;

      this.taskService.getHtmlReportAnnotations(this.taskInstanceId).subscribe({
         next: (apiAnnotations) => {
            this.annotations = this.normalizeAnnotations(apiAnnotations);
            this.cleanupOrphanAnnotations();
            this.rebuildMapsFromAnnotations();
            this.isAnnotationsLoading = false;
         },
         error: (err) => {
            this.isAnnotationsLoading = false;
            this.notifyError('Unable to load report annotations', err);

            // fallback so the viewer still works
            this.annotations = this.normalizeAnnotations(this.seedAnnotations);
            this.cleanupOrphanAnnotations();
            this.rebuildMapsFromAnnotations();
         }
      });
   }

   private normalizeAnnotations(input: any): HtmlReportAnnotations {
      if (!input) return EMPTY_HTML_REPORT_ANNOTATIONS;

      if (Array.isArray(input?.rows)) {
         return {
            rows: input.rows
               .filter((r: any) => !!r?.rowId)
               .map((r: any) => ({
                  rowId: String(r.rowId),
                  highlight: r.highlight ?? null,
                  comment: r.comment ?? null
               }))
         };
      }

      // Optional legacy support (if you ever had Option A)
      const highlights = input?.highlights ?? {};
      const comments = input?.comments ?? {};

      const rowIds = new Set<string>([
         ...Object.keys(highlights),
         ...Object.keys(comments)
      ]);

      return {
         rows: Array.from(rowIds).map((rowId) => ({
            rowId,
            highlight: highlights[rowId] ? { color: highlights[rowId].color } : null,
            comment: comments[rowId]
               ? {
                    text: comments[rowId].text,
                    updatedAtIso: comments[rowId].updatedAtIso
                 }
               : null
         }))
      };
   }
}
